import {
  AlertTriangle,
  Shield,
  Clock,
  Users,
  Cloud,
  Cpu,
  BookOpen,
  ChevronDown,
  Copy,
  Activity,
  Target,
  Zap,
} from "lucide-react";
import { Card, CardContent } from "../../../ui/common-ui/card";
import { Separator } from "../../../ui/common-ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/common-ui/collapsible";
import { Button } from "../../../ui/common-ui/button";
import { useState } from "react";
import { GraphNode } from "../types/graph";
import { Badge } from "../../../ui/common-ui/badge";

interface AlertInspectorProps {
  node: GraphNode;
}

// Format date to readable format
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Get severity badge variant
const getSeverityBadgeClass = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case "critical":
    case "high":
      return "border-destructive/40 bg-destructive/10 text-destructive";
    case "medium":
      return "border-primary/30 bg-primary/10 text-primary";
    case "low":
      return "border-secondary/50 bg-secondary/30 text-secondary-foreground";
    default:
      return "border-border bg-muted text-muted-foreground";
  }
};

export function AlertInspector({ node }: AlertInspectorProps) {
  const [attackTimelineOpen, setAttackTimelineOpen] = useState(true);
  const [analysisOpen, setAnalysisOpen] = useState(true);
  const [mitreOpen, setMitreOpen] = useState(true);

  const props = node.props || {};
  const severity = props.severity || "medium";
  const alertType = props.alertType || "RULE";
  const entity = props.entity;
  const cloudType = props.cloudType || "GCP";
  const attackPath = props.attackPath || {};
  const info = props.info || {};
  const mitre = props.mitre || {};
  const affectedResources = props.affectedResources || [];
  const timeRangeStart = props.timeRangeStart;
  const timeRangeEnd = props.timeRangeEnd;
  const createdAt = props.createdAt;
  const mergedAlertCount = props.mergedAlertCount || 1;
  const isMergedAlert = props.isMergedAlert === true && mergedAlertCount > 1;

  const attackSteps = Object.keys(attackPath).length;
  const attackEntries = Object.entries(attackPath as Record<string, any>).sort(([a], [b]) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-4">
      {/* Alert Header with Badges */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Badge
            variant="outline"
            className="text-xs font-medium flex items-center gap-1.5 px-2.5 py-1"
          >
            <BookOpen className="w-3 h-3" />
            {alertType}
          </Badge>
          <Badge
            variant="outline"
            className={`text-xs font-medium flex items-center gap-1.5 px-2.5 py-1 ${getSeverityBadgeClass(severity)}`}
          >
            <AlertTriangle className="w-3 h-3" />
            {severity.charAt(0).toUpperCase() + severity.slice(1)}
          </Badge>
          {isMergedAlert && (
            <Badge
              variant="outline"
              className="text-xs font-medium flex items-center gap-1.5 px-2.5 py-1 border-destructive/40 bg-destructive/10 text-destructive"
            >
              <Activity className="w-3 h-3" />
              {mergedAlertCount} alerts
            </Badge>
          )}
        </div>

        {/* Alert Title */}
        <h3 className="text-lg font-semibold text-foreground leading-tight">
          {node.label}
        </h3>

        {/* Attack Steps Count */}
        {attackSteps > 0 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Target className="w-3 h-3" />
            {attackSteps} attack step{attackSteps !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      <Separator className="bg-border/50" />

      {/* Entity / Cloud Provider / Alert Type (top-down) */}
      <div className="space-y-3">
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Entity
          </p>
          <div className="flex items-start gap-2">
            <Users className="w-3.5 h-3.5 text-muted-foreground mt-0.5" />
            <span className="text-xs font-medium text-foreground break-all">
              {entity || "N/A"}
            </span>
            {entity && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-muted/50 -mt-0.5"
                onClick={() => navigator.clipboard.writeText(entity)}
                aria-label="Copy entity"
              >
                <Copy className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Cloud Provider
          </p>
          <div className="flex items-center gap-2">
            <Cloud className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">{cloudType}</span>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Alert Type
          </p>
          <div className="flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">{alertType}</span>
          </div>
        </div>
      </div>

      <Separator className="bg-border/50" />

      {/* Attack Timeline Section */}
      {attackSteps > 0 && (
        <Collapsible open={attackTimelineOpen} onOpenChange={setAttackTimelineOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/30 -mx-2 px-2 rounded-lg transition-colors">
            <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" />
              Attack Timeline
            </h4>
            <ChevronDown
              className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${attackTimelineOpen ? "rotate-180" : ""
                }`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-2">
            {attackEntries.map(([stepNum, step]: [string, any]) => (
              <Card
                key={stepNum}
                className="border-border/40 bg-gradient-to-br from-muted/30 to-muted/10 shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <CardContent className="p-3 space-y-2.5">
                  {/* Step Header */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">
                        {Number.isNaN(Number(stepNum)) ? stepNum : Number(stepNum)}
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Zap className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="text-xs font-medium text-foreground break-all">
                          {step.action || "Unknown Action"}
                        </span>
                      </div>
                    </div>
                    <div className="pl-7">
                      <span className="text-[10px] text-muted-foreground">
                        {formatDate(step.time)}
                      </span>
                    </div>
                  </div>

                  {/* Step Details */}
                  <div className="space-y-1.5 pl-7">
                    {step.resource && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-14">Resource</span>
                        <span className="text-xs font-mono bg-background/60 px-2 py-0.5 rounded border border-border/30 break-all">
                          {step.resource}
                        </span>
                      </div>
                    )}
                    {step.ip && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-14">IP Address</span>
                        <span className="text-xs font-mono bg-background/60 px-2 py-0.5 rounded border border-border/30 break-all">
                          {step.ip}
                        </span>
                      </div>
                    )}
                    {step.region && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-14">Region</span>
                        <span className="text-xs font-mono bg-background/60 px-2 py-0.5 rounded border border-border/30 break-all">
                          {step.region}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      <Separator className="bg-border/50" />

      {/* Analysis Section */}
      <Collapsible open={analysisOpen} onOpenChange={setAnalysisOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/30 -mx-2 px-2 rounded-lg transition-colors">
          <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" />
            Analysis
          </h4>
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${analysisOpen ? "rotate-180" : ""
              }`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 pt-2">
          {/* Why this matters */}
          {info.why && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-foreground">Why this matters</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{info.why}</p>
            </div>
          )}

          {/* What happened */}
          {info.what && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-foreground">What happened</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{info.what}</p>
            </div>
          )}

          {/* Recommended actions */}
          {info.how && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-foreground">Recommended actions</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{info.how}</p>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Separator className="bg-border/50" />

      {/* MITRE ATT&CK Techniques */}
      {Object.keys(mitre).length > 0 && (
        <>
          <Collapsible open={mitreOpen} onOpenChange={setMitreOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/30 -mx-2 px-2 rounded-lg transition-colors">
              <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-2">
                <Target className="w-3.5 h-3.5" />
                MITRE ATT&CK Techniques
              </h4>
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${mitreOpen ? "rotate-180" : ""
                  }`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="flex flex-wrap gap-2">
                {Object.entries(mitre).map(([techniqueId, techniqueName]) => (
                  <Badge
                    key={techniqueId}
                    variant="outline"
                    className="text-xs font-mono px-2.5 py-1 border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    {techniqueId}: {techniqueName as string}
                  </Badge>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Separator className="bg-border/50" />
        </>
      )}

      {/* Detection Timeline */}
      <div className="space-y-2">
        <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" />
          Detection Timeline
        </h4>
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30 space-y-1">
            <p className="text-[10px] text-muted-foreground">Window Start</p>
            <p className="text-xs font-medium">{formatDate(timeRangeStart)}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30 space-y-1">
            <p className="text-[10px] text-muted-foreground">Window End</p>
            <p className="text-xs font-medium">{formatDate(timeRangeEnd)}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/30 border border-border/30 space-y-1">
            <p className="text-[10px] text-muted-foreground">{isMergedAlert ? "Latest Alert" : "Alert Created"}</p>
            <p className="text-xs font-medium">{formatDate(createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Affected Resources */}
      {affectedResources.length > 0 && (
        <>
          <Separator className="bg-border/50" />
          <div className="space-y-2">
            <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5" />
              Affected Resources
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {affectedResources.map((resource: string, idx: number) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className="text-xs font-mono px-2 py-0.5 border-border/50 bg-background/60"
                >
                  {resource}
                </Badge>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
